/**
 * The operation catalogue — the one registry every Genie surface reads.
 *
 * A node in a workflow, an app manifest's `execution.operation` and a frame tool are three
 * ways to reach the same operation. This file is the single definition of what those
 * operations are, what they accept and what they return; hosts add presentation, routing and
 * pricing on top. The desktop engine's DAG runtime imports these verbatim (see
 * `scripts/sync-graph-schemas.mjs` in the desktop repo), so adding an operation here is what
 * makes it executable, offerable in the node palette and legal in an app package.
 *
 * Port types drive edge validation in the editor; at runtime every edge carries items[].
 */
import type { GraphExposedParameter, GraphNodeDef } from './index';

export interface GraphOperationDef extends GraphNodeDef {
  /** May back a canvas app manifest's `execution.operation` (mirrored by AppOperationSchema). */
  app?: boolean;
  /** Accepts a saved model recipe, so `params.generation` exists and hosts render a model picker. */
  generation?: boolean;
}

/** Operations that accept a model recipe. `genie-kit`'s APP_GENERATION_OPERATIONS maps these to engine modes. */
export const GENERATION_OPERATIONS = [
  'gen.image', 'gen.video', 'gen.sound', 'gen.3d',
  'edit.instruct', 'edit.variation', 'edit.inpaint', 'edit.expand', 'edit.detail', 'edit.upscale',
] as const;

const DEFS: Record<string, GraphOperationDef> = {
  'trigger.manual': {
    label: 'Manual', category: 'Triggers', icon: 'play',
    inputs: [], outputs: [{ id: 'out', type: 'any' }],
    params: {},
  },
  'trigger.schedule': {
    label: 'Schedule', category: 'Triggers', icon: 'clock',
    inputs: [], outputs: [{ id: 'out', type: 'any' }],
    params: { everyMinutes: 60 },
  },
  'trigger.watch': {
    label: 'Watch folder', category: 'Triggers', icon: 'eye',
    inputs: [], outputs: [{ id: 'out', type: 'image' }],
    params: { path: 'inbox' },
  },
  'canvas.in': {
    label: 'Canvas In', category: 'Canvas', icon: 'frame',
    inputs: [], outputs: [{ id: 'out', type: 'image' }],
    params: { artboardId: '' },
  },
  // The pictures a section holds (Board frames inside its box) or a Compositor holds (its layers); frameIds
  // (comma-separated) narrows them, or stands alone as the frames to work on.
  'canvas.section': {
    label: 'Section In', category: 'Canvas', icon: 'section',
    inputs: [], outputs: [{ id: 'out', type: 'image' }],
    params: { sectionId: '', frameIds: '' },
  },
  'canvas.note': {
    label: 'Note In', category: 'Canvas', icon: 'note',
    inputs: [], outputs: [{ id: 'out', type: 'text' }],
    params: { artboardId: '' },
  },
  // Every frame that carries a note (a note anchored to it), the note's words riding on the item as its
  // instruction; the edit nodes take them when their own prompt is empty. A section narrows it.
  'canvas.notes': {
    label: 'Notes In', category: 'Canvas', icon: 'note',
    inputs: [], outputs: [{ id: 'out', type: 'image' }],
    params: { sectionId: '' },
  },
  'canvas.out': {
    label: 'Canvas Out', category: 'Canvas', icon: 'frame',
    inputs: [{ id: 'in', type: 'image' }], outputs: [],
    params: { mode: 'new', artboardId: '' }, // 'new' artboards | 'stack' versions on one frame | 'source' back on each item's own frame
  },
  'gen.image': {
    label: 'Generate image', category: 'Generate', icon: 'sparkles', app: true, generation: true,
    inputs: [{ id: 'in', type: 'text' }, { id: 'ref', type: 'image' }, { id: 'control', type: 'image' }],
    outputs: [{ id: 'out', type: 'image' }],
    params: { prompt: '', width: 512, height: 512, count: 1, provider: '', model: '', engine: '', control: '' },
  },
  'gen.video': {
    label: 'Animate', category: 'Generate', icon: 'film', app: true, generation: true,
    inputs: [{ id: 'in', type: 'image' }, { id: 'text', type: 'text' }], outputs: [{ id: 'out', type: 'video' }],
    params: { prompt: '', seconds: 5, engine: '' },
  },
  'gen.sound': {
    label: 'Sound', category: 'Generate', icon: 'music', app: true, generation: true,
    inputs: [{ id: 'in', type: 'text' }], outputs: [{ id: 'out', type: 'audio' }],
    params: { prompt: '', seconds: 10, engine: '' },
  },
  'gen.3d': {
    label: 'Image → 3D', category: 'Generate', icon: 'box', app: true, generation: true,
    inputs: [{ id: 'in', type: 'image' }], outputs: [{ id: 'out', type: 'mesh' }],
    params: { engine: 'cloud' },
  },
  'edit.instruct': {
    label: 'Edit with words', category: 'Edit', icon: 'wand', app: true, generation: true,
    inputs: [{ id: 'in', type: 'image' }, { id: 'ref', type: 'image' }], outputs: [{ id: 'out', type: 'image' }],
    params: { prompt: '', engine: '', keepRest: false, context: false },
  },
  'edit.erase': {
    label: 'Erase', category: 'Edit', icon: 'eraser', app: true,
    inputs: [{ id: 'in', type: 'image' }, { id: 'mask', type: 'image' }], outputs: [{ id: 'out', type: 'image' }],
    params: { engine: '', keepRest: true },
  },
  // Cut by a selection, no model: keep the selection (the rest transparent) or cut it out, the edge feathered.
  'edit.cutout': {
    label: 'Cut out', category: 'Edit', icon: 'scissors', app: true,
    inputs: [{ id: 'in', type: 'image' }, { id: 'mask', type: 'image' }], outputs: [{ id: 'out', type: 'image' }],
    params: { mode: 'keep', feather: 2 },
  },
  'edit.segment': {
    label: 'Select subject', category: 'Edit', icon: 'scan', app: true,
    inputs: [{ id: 'in', type: 'image' }], outputs: [{ id: 'out', type: 'image' }],
    params: { prompt: '', points: '', engine: '' },
  },
  'edit.detail': {
    label: 'Face detail', category: 'Edit', icon: 'smile', app: true, generation: true,
    inputs: [{ id: 'in', type: 'image' }], outputs: [{ id: 'out', type: 'image' }],
    params: { prompt: '', strength: 0.4, engine: '' },
  },
  'edit.interpolate': {
    label: 'Smooth motion', category: 'Edit', icon: 'fast-forward', app: true,
    inputs: [{ id: 'in', type: 'video' }], outputs: [{ id: 'out', type: 'video' }],
    params: { multiplier: 2, engine: '' },
  },
  'edit.vectorize': {
    label: 'Vectorize', category: 'Edit', icon: 'pen-tool', app: true,
    inputs: [{ id: 'in', type: 'image' }], outputs: [{ id: 'out', type: 'image' }],
    params: { colors: 16, detail: 'balanced', engine: '' },
  },
  'text.describe': {
    label: 'Describe image', category: 'Text', icon: 'eye', app: true,
    inputs: [{ id: 'in', type: 'image' }], outputs: [{ id: 'out', type: 'text' }],
    params: { engine: '', model: '' }, // model: a GET /prompt-tools choice id; empty = the usual order
  },
  'text.enhance': {
    label: 'Enhance prompt', category: 'Text', icon: 'sparkle', app: true,
    inputs: [{ id: 'in', type: 'text' }], outputs: [{ id: 'out', type: 'text' }],
    params: { style: '', model: '' },
  },
  // A hosted model tool from the engine's hosted-tool table; `tool` is its slug, the rest are that tool's options.
  'tool.run': {
    label: 'Model tool', category: 'Generate', icon: 'wand-2', app: true,
    inputs: [{ id: 'in', type: 'any' }, { id: 'ref', type: 'any' }, { id: 'audio', type: 'any' }, { id: 'video', type: 'any' }], outputs: [{ id: 'out', type: 'any' }],
    params: { tool: '', model: '', prompt: '', lighting: '', garment: '', pose: '', scribble: false, influence: 0.75, quality: '', scale: '', direction: '', seconds: 5, aspect: '', task: '', language: '', voice: '', polycount: 30000 },
  },
  'edit.variation': {
    label: 'Variation', category: 'Edit', icon: 'shuffle', app: true, generation: true,
    inputs: [{ id: 'in', type: 'image' }], outputs: [{ id: 'out', type: 'image' }],
    params: { creativity: 0.5, prompt: '' },
  },
  'edit.inpaint': {
    label: 'Inpaint', category: 'Edit', icon: 'paintbrush', app: true, generation: true,
    inputs: [{ id: 'in', type: 'image' }, { id: 'mask', type: 'image' }], outputs: [{ id: 'out', type: 'image' }],
    params: { prompt: '', denoise: 1, growMask: 8, keepRest: true },
  },
  'edit.move': {
    label: 'Move object', category: 'Edit', icon: 'move',
    inputs: [{ id: 'in', type: 'image' }, { id: 'mask', type: 'image' }], outputs: [{ id: 'out', type: 'image' }],
    params: { dx: 0, dy: 0, blend: true },
  },
  'edit.expand': {
    label: 'Expand', category: 'Edit', icon: 'expand', app: true, generation: true,
    inputs: [{ id: 'in', type: 'image' }], outputs: [{ id: 'out', type: 'image' }],
    params: { left: 0, right: 0, top: 0, bottom: 0, prompt: '' },
  },
  'edit.upscale': {
    label: 'Upscale', category: 'Edit', icon: 'arrow-up', app: true, generation: true,
    inputs: [{ id: 'in', type: 'image' }], outputs: [{ id: 'out', type: 'image' }],
    params: { scale: 2, method: 'fast', creativity: 0.3, engine: '', tiles: 'auto' },
  },
  'edit.rembg': {
    label: 'Remove background', category: 'Edit', icon: 'eraser', app: true,
    inputs: [{ id: 'in', type: 'image' }, { id: 'ref', type: 'image' }], outputs: [{ id: 'out', type: 'image' }],
    params: { engine: '', beneath: false },
  },
  'edit.bgfree': {
    label: 'Repaint without background', category: 'Edit', icon: 'sparkles', app: true,
    inputs: [{ id: 'in', type: 'image' }], outputs: [{ id: 'out', type: 'image' }],
    params: { prompt: '', match: true, fallback: 'cut' },
  },
  'edit.adjust': {
    label: 'Adjust', category: 'Edit', icon: 'sliders', app: true,
    inputs: [{ id: 'in', type: 'image' }], outputs: [{ id: 'out', type: 'image' }],
    params: { brightness: 1, contrast: 1, saturation: 1, hue: 0, temperature: 0, blur: 0, sharpen: 0, grayscale: false },
  },
  // Cut-out care: instant, no model. What a lifted subject needs afterwards.
  'edit.alpha': {
    label: 'Clean cut-out', category: 'Edit', icon: 'eraser', app: true,
    inputs: [{ id: 'in', type: 'image' }], outputs: [{ id: 'out', type: 'image' }],
    params: { specks: 400, faint: true, defringe: true, edge: 'none' }, // specks: area in px, 0 keeps them; edge: none | soften | harden
  },
  'edit.match': {
    label: 'Match tone', category: 'Edit', icon: 'palette', app: true,
    inputs: [{ id: 'in', type: 'image' }, { id: 'ref', type: 'image' }], outputs: [{ id: 'out', type: 'image' }],
    // strength: how far the mean colour moves toward the reference, 0..1. beneath: the reference is the ring round
    // the frame on the board. when: 'always', or 'off' (only a frame flagged tone or much brighter than the ring).
    params: { strength: 0.5, beneath: false, when: 'always' },
  },
  'edit.shadow': {
    label: 'Contact shadow', category: 'Edit', icon: 'sun-dim', app: true,
    inputs: [{ id: 'in', type: 'image' }], outputs: [{ id: 'out', type: 'image' }],
    // of the cut-out's width; the shadow is its own layer. ground: write it as the frame's ground layer (when it has none) and pass the picture through.
    params: { strength: 0.34, width: 0.36, height: 0.075, ground: false },
  },
  // The night grade: a frame's mid-tones pulled toward the board's light at its foot, highlights kept, a share of the cast, never brightening.
  'edit.grade': {
    label: 'Grade to light', category: 'Edit', icon: 'sun-dim', app: true,
    inputs: [{ id: 'in', type: 'image' }, { id: 'ref', type: 'image' }], outputs: [{ id: 'out', type: 'image' }],
    params: { strength: 0.75, lift: 14, cast: 0.4 }, // ref: the picture the light field is read from; without one, the board's background frame
  },
  // An audit over frames: flags written on them (bg, cut, tone, ground, foot, lost), the numbers in the item's meta, the picture passed through.
  'edit.inspect': {
    label: 'Inspect', category: 'Edit', icon: 'search', app: true,
    inputs: [{ id: 'in', type: 'image' }], outputs: [{ id: 'out', type: 'image' }],
    params: { checks: 'bg,cut,tone,ground,foot,lost', write: true },
  },
  // Data Dune: the desktop places a live WebGPU frame for it; the engine DAG cannot execute it.
  'render.pigment': {
    label: 'Data Dune', category: 'Edit', icon: 'wind', app: true,
    inputs: [{ id: 'in', type: 'image' }], outputs: [{ id: 'out', type: 'image' }],
    params: { look: 'drift', turbulence: 1.5, wanderRadius: 0.075, masterSize: 0.92, relief: 0.9, climbHeight: 0.3, seed: 1 },
  },
  'edit.crop': {
    label: 'Crop', category: 'Edit', icon: 'crop', app: true,
    inputs: [{ id: 'in', type: 'image' }], outputs: [{ id: 'out', type: 'image' }],
    params: { x: 0, y: 0, w: 1, h: 1 }, // fractions of the source
  },
  'edit.transform': {
    label: 'Flip & rotate', category: 'Edit', icon: 'rotate', app: true,
    inputs: [{ id: 'in', type: 'image' }], outputs: [{ id: 'out', type: 'image' }],
    params: { flipH: false, flipV: false, rotate: 0 },
  },
  'edit.split-grid': {
    label: 'Split image grid', category: 'Edit', icon: 'grid', app: true,
    inputs: [{ id: 'in', type: 'image' }], outputs: [{ id: 'out', type: 'image' }],
    params: { columns: 2, rows: 2 },
  },
  // Retired from the palette: stacking images is the Layers editor now. Saved graphs keep running.
  'compose.composite': {
    label: 'Composite', category: 'Compose', icon: 'layers', legacy: true, app: true,
    inputs: [{ id: 'a', type: 'image' }, { id: 'b', type: 'image' }],
    outputs: [{ id: 'out', type: 'image' }],
    params: { anchor: 'center', scalePct: 50, opacity: 1, blend: 'over' }, // a = base, b = overlay
  },
  'compose.text': {
    label: 'Text overlay', category: 'Compose', icon: 'type', app: true,
    inputs: [{ id: 'in', type: 'image' }], outputs: [{ id: 'out', type: 'image' }],
    params: { text: '', size: 64, color: '#ffffff', anchor: 'bottom', bold: true }, // {{text}} → item.text
  },
  'compose.resize': {
    label: 'Resize', category: 'Compose', icon: 'maximize', app: true,
    inputs: [{ id: 'in', type: 'image' }], outputs: [{ id: 'out', type: 'image' }],
    params: { width: 1024, height: 1024, fit: 'cover' },
  },
  'compose.adjust': {
    label: 'Adjust', category: 'Compose', icon: 'sliders',
    inputs: [{ id: 'in', type: 'image' }], outputs: [{ id: 'out', type: 'image' }],
    params: { brightness: 1, saturation: 1, blur: 0, grayscale: false },
  },
  'text.prompt': {
    label: 'Prompt', category: 'Text', icon: 'type',
    inputs: [{ id: 'in', type: 'text' }], outputs: [{ id: 'out', type: 'text' }],
    params: { text: '' }, // {{text}} is replaced with the incoming item's text
  },
  'logic.if': {
    label: 'Filter', category: 'Logic', icon: 'filter',
    inputs: [{ id: 'in', type: 'any' }],
    outputs: [{ id: 'yes', type: 'any' }, { id: 'no', type: 'any' }],
    params: { contains: '' }, // matched against text / title / prompt
  },
  'logic.merge': {
    label: 'Merge', category: 'Logic', icon: 'merge',
    inputs: [{ id: 'a', type: 'any' }, { id: 'b', type: 'any' }], outputs: [{ id: 'out', type: 'any' }],
    params: {},
  },
  'files.read': {
    label: 'Read folder', category: 'Files', icon: 'folder',
    inputs: [], outputs: [{ id: 'out', type: 'image' }],
    params: { path: 'outputs' },
  },
  'files.save': {
    label: 'Save to folder', category: 'Files', icon: 'save',
    inputs: [{ id: 'in', type: 'image' }], outputs: [],
    params: { path: 'exports' },
  },
};

// Model recipes are available to Apps and to the same executable nodes.
for (const operation of GENERATION_OPERATIONS) DEFS[operation].params.generation = null;

/** Every operation this platform knows how to run, keyed by node type. */
export const GRAPH_NODE_DEFS: Record<string, GraphOperationDef> = DEFS;

/** Operations an app package may declare. `AppOperationSchema` mirrors this list exactly. */
export const APP_OPERATIONS: string[] = Object.keys(DEFS).filter((type) => DEFS[type].app);

/** A definition by node type, or undefined for a type this build does not know. */
export function graphNodeDef(type: string): GraphOperationDef | undefined {
  return Object.hasOwn(DEFS, type) ? DEFS[type] : undefined;
}

/**
 * A ComfyUI workflow imported into a session appears as `comfy.custom:<id>` with its exposed
 * params. The host supplies the workflow; the shape it becomes is defined here so the editor,
 * the validator and the runtime agree on it.
 */
export interface CustomWorkflow {
  id: string;
  name: string;
  inputs?: { image?: unknown };
  output?: 'image' | 'video' | 'audio' | 'mesh';
  params?: GraphExposedParameter[];
}
export function customWorkflowDef(workflow: CustomWorkflow): GraphOperationDef {
  const params: Record<string, unknown> = {};
  for (const item of workflow.params ?? []) params[item.key] = item.value;
  return {
    label: workflow.name, category: 'ComfyUI', icon: 'workflow', custom: workflow.id,
    inputs: [...(workflow.inputs?.image ? [{ id: 'in', type: 'image' as const }] : []), { id: 'text', type: 'text' as const }],
    outputs: [{ id: 'out', type: workflow.output ?? 'image' }],
    params, exposed: workflow.params ?? [],
  };
}

export const CUSTOM_NODE_PREFIX = 'comfy.custom:';

/**
 * Resolve a node type against the built-in catalogue, then the session's imported workflows.
 * Lookups are own-property only: a saved graph may name any type, including `constructor` or
 * `toString`, and those must come back as unknown rather than as something off Object.prototype.
 */
export function resolveNodeDef(type: string, custom: Record<string, CustomWorkflow> = {}): GraphOperationDef | null {
  const builtin = graphNodeDef(type);
  if (builtin) return builtin;
  if (!type.startsWith(CUSTOM_NODE_PREFIX)) return null;
  const id = type.slice(CUSTOM_NODE_PREFIX.length);
  return Object.hasOwn(custom, id) ? customWorkflowDef(custom[id]) : null;
}

/** The whole palette for one session: the built-in operations plus its imported workflows. */
export function graphNodeDefs(custom: Record<string, CustomWorkflow> = {}): Record<string, GraphOperationDef> {
  const defs: Record<string, GraphOperationDef> = { ...GRAPH_NODE_DEFS };
  for (const id of Object.keys(custom)) defs[`${CUSTOM_NODE_PREFIX}${id}`] = customWorkflowDef(custom[id]!);
  return defs;
}
